/**
 * Pilot customer interview form.
 * Public page (no auth required) — shareable link at /interview.
 * 5-step wizard capturing regulatory insights, pain points, and data validation.
 */
import { useState, useEffect } from "react";
import { ChevronRight, ChevronLeft, Send, CheckCircle, Star, ThumbsUp, ThumbsDown, Loader2 } from "lucide-react";

const COUNTRIES = [
  { code: "KE", name: "Kenya" },
  { code: "NG", name: "Nigeria" },
  { code: "ZA", name: "South Africa" },
  { code: "GH", name: "Ghana" },
  { code: "NA", name: "Namibia" },
  { code: "ZM", name: "Zambia" },
];

const PROJECT_TYPES = ["Solar PV (utility-scale)", "Solar mini-grid", "Solar + storage hybrid", "Wind", "Other"];
const CAPACITY_RANGES = ["< 100 kW", "100 kW – 1 MW", "1 – 10 MW", "10 – 50 MW", "50 MW+"];

const DIFFICULTY_PHASES = [
  { key: "company_setup", label: "Company setup / SPV formation" },
  { key: "land_acquisition", label: "Land acquisition" },
  { key: "environmental_eia", label: "Environmental approval (EIA)" },
  { key: "energy_license", label: "Energy generation license" },
  { key: "grid_connection", label: "Grid connection" },
  { key: "construction_permits", label: "Construction permits" },
  { key: "financing", label: "Financing / financial close" },
  { key: "commissioning", label: "Commissioning" },
];

const steps = ["About You", "Pain Points", "Data Check", "Operations", "Next Steps"];

interface InterviewState {
  // Step 1
  name: string;
  email: string;
  org_name: string;
  country_code: string;
  project_type: string;
  capacity_range: string;
  // Step 2
  surprising_requirement: string;
  longest_approval: string;
  project_killer: string;
  difficulty_ratings: Record<string, number>;
  // Step 3
  compliance_ratings: Record<string, "up" | "down" | null>;
  missing_items: string;
  not_applicable: string[];
  // Step 4
  time_consuming_compliance: string;
  tracking_method: string;
  regulatory_surprise: string;
  regulatory_surprise_details: string;
  // Step 5
  interested_in_platform: string;
  follow_up_call: string;
  follow_up_time: string;
  anything_else: string;
}

const initialState: InterviewState = {
  name: "", email: "", org_name: "", country_code: "", project_type: "", capacity_range: "",
  surprising_requirement: "", longest_approval: "", project_killer: "",
  difficulty_ratings: {},
  compliance_ratings: {}, missing_items: "", not_applicable: [],
  time_consuming_compliance: "", tracking_method: "", regulatory_surprise: "", regulatory_surprise_details: "",
  interested_in_platform: "", follow_up_call: "", follow_up_time: "", anything_else: "",
};

export default function InterviewForm() {
  const [step, setStep] = useState(0);
  const [state, setState] = useState<InterviewState>(initialState);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [complianceItems, setComplianceItems] = useState<any[]>([]);

  // Fetch compliance items when country changes (for Step 3)
  useEffect(() => {
    if (state.country_code) {
      fetch(`/api/compliance/${state.country_code}?track=government`)
        .then(r => r.ok ? r.json() : [])
        .then(items => setComplianceItems((items || []).slice(0, 10)))
        .catch(() => setComplianceItems([]));
    }
  }, [state.country_code]);

  const update = (patch: Partial<InterviewState>) => setState(s => ({ ...s, ...patch }));

  const canAdvance = () => {
    if (step === 0) return state.email.includes("@");
    return true; // all other steps are optional
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const res = await fetch("/api/interview", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: state.name,
          email: state.email,
          org_name: state.org_name,
          country_code: state.country_code,
          project_type: state.project_type,
          capacity_range: state.capacity_range,
          answers: {
            surprising_requirement: state.surprising_requirement,
            longest_approval: state.longest_approval,
            project_killer: state.project_killer,
            difficulty_ratings: state.difficulty_ratings,
            compliance_ratings: state.compliance_ratings,
            missing_items: state.missing_items,
            not_applicable: state.not_applicable,
            time_consuming_compliance: state.time_consuming_compliance,
            tracking_method: state.tracking_method,
            regulatory_surprise: state.regulatory_surprise,
            regulatory_surprise_details: state.regulatory_surprise_details,
            interested_in_platform: state.interested_in_platform,
            follow_up_call: state.follow_up_call,
            follow_up_time: state.follow_up_time,
            anything_else: state.anything_else,
          },
        }),
      });
      if (res.ok) setSubmitted(true);
    } catch {
      // silently handle — still show thanks
      setSubmitted(true);
    }
    setSubmitting(false);
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-xl shadow-sm border p-8 text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Thank you!</h1>
          <p className="text-gray-600 mb-6">Your insights are incredibly valuable. We're building enereg to make regulatory compliance painless for developers like you.</p>
          <a href="/" className="inline-block px-6 py-3 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors">
            Explore enereg
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-2xl mx-auto px-6 py-4">
          <h1 className="text-lg font-bold text-gray-900">enereg — Developer Insights</h1>
          <p className="text-sm text-gray-500">Help us build the regulatory compliance tool you wish existed</p>
        </div>
      </div>

      {/* Progress bar */}
      <div className="max-w-2xl mx-auto px-6 pt-6">
        <div className="flex items-center gap-1 mb-1">
          {steps.map((s, i) => (
            <div key={s} className={`flex-1 h-1.5 rounded-full transition-colors ${i <= step ? "bg-indigo-500" : "bg-gray-200"}`} />
          ))}
        </div>
        <div className="flex justify-between text-xs text-gray-500">
          <span>Step {step + 1} of {steps.length}</span>
          <span>{steps[step]}</span>
        </div>
      </div>

      {/* Form content */}
      <div className="max-w-2xl mx-auto px-6 py-8">
        <div className="bg-white rounded-xl shadow-sm border p-6 min-h-[400px]">

          {/* Step 1: About You */}
          {step === 0 && (
            <div className="space-y-5">
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-1">About You</h2>
                <p className="text-sm text-gray-500">Quick context so we can tailor the questions. Only email is required.</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input type="text" value={state.name} onChange={e => update({ name: e.target.value })} placeholder="Optional" className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                  <input type="email" value={state.email} onChange={e => update({ email: e.target.value })} placeholder="you@company.com" className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Organization</label>
                <input type="text" value={state.org_name} onChange={e => update({ org_name: e.target.value })} placeholder="Company or project name" className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Country of operation</label>
                <div className="flex flex-wrap gap-2">
                  {COUNTRIES.map(c => (
                    <button key={c.code} onClick={() => update({ country_code: c.code })} className={`px-4 py-2 rounded-lg text-sm border transition-colors ${state.country_code === c.code ? "bg-indigo-50 border-indigo-500 text-indigo-700 font-medium" : "bg-white border-gray-200 text-gray-700 hover:border-gray-300"}`}>{c.name}</button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Project type</label>
                  <select value={state.project_type} onChange={e => update({ project_type: e.target.value })} className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500">
                    <option value="">Select...</option>
                    {PROJECT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Capacity</label>
                  <select value={state.capacity_range} onChange={e => update({ capacity_range: e.target.value })} className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500">
                    <option value="">Select...</option>
                    {CAPACITY_RANGES.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Pain Points */}
          {step === 1 && (
            <div className="space-y-5">
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-1">Regulatory Pain Points</h2>
                <p className="text-sm text-gray-500">Your real experience is more valuable than any legal database. All answers are optional.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">What was the most surprising regulatory requirement you encountered?</label>
                <textarea value={state.surprising_requirement} onChange={e => update({ surprising_requirement: e.target.value })} rows={3} placeholder="The one nobody warned you about..." className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Which approval took the longest? How long?</label>
                <textarea value={state.longest_approval} onChange={e => update({ longest_approval: e.target.value })} rows={2} placeholder="e.g., EIA took 14 months instead of the statutory 3..." className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">What nearly killed or delayed your project?</label>
                <textarea value={state.project_killer} onChange={e => update({ project_killer: e.target.value })} rows={2} placeholder="The thing that almost stopped the project..." className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Rate the difficulty of each phase (1 = easy, 5 = extremely difficult)</label>
                <div className="space-y-2">
                  {DIFFICULTY_PHASES.map(phase => (
                    <div key={phase.key} className="flex items-center justify-between">
                      <span className="text-sm text-gray-700">{phase.label}</span>
                      <div className="flex gap-1">
                        {[1, 2, 3, 4, 5].map(n => (
                          <button key={n} onClick={() => update({ difficulty_ratings: { ...state.difficulty_ratings, [phase.key]: n } })} className="p-1">
                            <Star className={`w-5 h-5 transition-colors ${(state.difficulty_ratings[phase.key] ?? 0) >= n ? "text-amber-400 fill-amber-400" : "text-gray-300"}`} />
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Data Validation */}
          {step === 2 && (
            <div className="space-y-5">
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-1">Data Check</h2>
                <p className="text-sm text-gray-500">
                  {state.country_code
                    ? `Here are the key government approvals we track for ${COUNTRIES.find(c => c.code === state.country_code)?.name}. Do they match your experience?`
                    : "Select a country in Step 1 to see compliance items to validate."}
                </p>
              </div>
              {complianceItems.length > 0 ? (
                <div className="space-y-2">
                  {complianceItems.map((item: any) => (
                    <div key={item.id} className="flex items-start gap-3 p-3 rounded-lg border bg-gray-50">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900">{item.display_name}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{item.approval_body} &middot; {item.framework_title?.slice(0, 60)}</p>
                      </div>
                      <div className="flex gap-1 shrink-0">
                        <button onClick={() => update({ compliance_ratings: { ...state.compliance_ratings, [item.id]: "up" } })} className={`p-1.5 rounded-md transition-colors ${state.compliance_ratings[item.id] === "up" ? "bg-green-100 text-green-600" : "text-gray-400 hover:text-green-500 hover:bg-green-50"}`}>
                          <ThumbsUp className="w-4 h-4" />
                        </button>
                        <button onClick={() => update({ compliance_ratings: { ...state.compliance_ratings, [item.id]: "down" } })} className={`p-1.5 rounded-md transition-colors ${state.compliance_ratings[item.id] === "down" ? "bg-red-100 text-red-600" : "text-gray-400 hover:text-red-500 hover:bg-red-50"}`}>
                          <ThumbsDown className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-400 text-sm">
                  {state.country_code ? "Loading compliance data..." : "Select a country in Step 1 to see items here."}
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Anything missing from this list?</label>
                <textarea value={state.missing_items} onChange={e => update({ missing_items: e.target.value })} rows={2} placeholder="Approvals we missed..." className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
              </div>
            </div>
          )}

          {/* Step 4: Operations */}
          {step === 3 && (
            <div className="space-y-5">
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-1">Ongoing Operations</h2>
                <p className="text-sm text-gray-500">What happens after your project is built? This is the part nobody talks about.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">What ongoing compliance obligations consume the most time?</label>
                <textarea value={state.time_consuming_compliance} onChange={e => update({ time_consuming_compliance: e.target.value })} rows={3} placeholder="License renewals, reporting, audits..." className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">How do you currently track regulatory deadlines?</label>
                <div className="flex flex-wrap gap-2">
                  {["Spreadsheet", "Calendar reminders", "Consultant handles it", "Legal team", "We don't track them", "Other"].map(m => (
                    <button key={m} onClick={() => update({ tracking_method: m })} className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${state.tracking_method === m ? "bg-indigo-50 border-indigo-500 text-indigo-700" : "bg-white border-gray-200 text-gray-700 hover:border-gray-300"}`}>{m}</button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Have you been surprised by a regulatory change that affected your project?</label>
                <div className="flex gap-2 mb-2">
                  {["Yes", "No"].map(v => (
                    <button key={v} onClick={() => update({ regulatory_surprise: v })} className={`px-4 py-2 rounded-lg text-sm border transition-colors ${state.regulatory_surprise === v ? "bg-indigo-50 border-indigo-500 text-indigo-700" : "bg-white border-gray-200 text-gray-700 hover:border-gray-300"}`}>{v}</button>
                  ))}
                </div>
                {state.regulatory_surprise === "Yes" && (
                  <textarea value={state.regulatory_surprise_details} onChange={e => update({ regulatory_surprise_details: e.target.value })} rows={2} placeholder="What changed and how did it affect you?" className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
                )}
              </div>
            </div>
          )}

          {/* Step 5: Next Steps */}
          {step === 4 && (
            <div className="space-y-5">
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-1">Next Steps</h2>
                <p className="text-sm text-gray-500">Almost done. How can we stay connected?</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Would you use a platform that tracks all regulatory requirements for your project?</label>
                <div className="flex gap-2">
                  {["Yes, definitely", "Maybe, depends on price/features", "No, I'm fine with my current process"].map(v => (
                    <button key={v} onClick={() => update({ interested_in_platform: v })} className={`px-3 py-2 rounded-lg text-sm border transition-colors flex-1 ${state.interested_in_platform === v ? "bg-indigo-50 border-indigo-500 text-indigo-700" : "bg-white border-gray-200 text-gray-700 hover:border-gray-300"}`}>{v}</button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Can we follow up with a 15-minute call?</label>
                <div className="flex gap-2 mb-2">
                  {["Yes", "No"].map(v => (
                    <button key={v} onClick={() => update({ follow_up_call: v })} className={`px-4 py-2 rounded-lg text-sm border transition-colors ${state.follow_up_call === v ? "bg-indigo-50 border-indigo-500 text-indigo-700" : "bg-white border-gray-200 text-gray-700 hover:border-gray-300"}`}>{v}</button>
                  ))}
                </div>
                {state.follow_up_call === "Yes" && (
                  <input type="text" value={state.follow_up_time} onChange={e => update({ follow_up_time: e.target.value })} placeholder="Best day/time for a call?" className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Anything else you'd like to share?</label>
                <textarea value={state.anything_else} onChange={e => update({ anything_else: e.target.value })} rows={3} placeholder="Advice, warnings, feature requests..." className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
              </div>
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center mt-6">
          <button onClick={() => setStep(s => s - 1)} disabled={step === 0} className="flex items-center gap-1 px-4 py-2 text-sm text-gray-600 hover:text-gray-900 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
            <ChevronLeft className="w-4 h-4" /> Back
          </button>
          {step < steps.length - 1 ? (
            <button onClick={() => setStep(s => s + 1)} disabled={!canAdvance()} className="flex items-center gap-1 px-6 py-2.5 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
              Next <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button onClick={handleSubmit} disabled={submitting} className="flex items-center gap-2 px-6 py-2.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50 transition-colors">
              {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting...</> : <><Send className="w-4 h-4" /> Submit</>}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
